import { Component, ChangeDetectionStrategy, computed, inject, input, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { NOTIFICATIONS_STORE } from '@shared/definitions/stores/notifications-store.token';
import { getNotificationIcon, getNotificationColor } from '@shared/utils/notification-display.util';
import {
  Notification,
  NotificationSummary,
  NotificationGrouping} from '@shared/definitions/notification.interface';
import {
  NotificationCategory,
  NotificationPriority
} from '@shared/definitions/notification.interface';
import { UIFeedbackService } from '../../../services/ui-feedback.service';

@Component({
    selector: 'app-notification-list',
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatBadgeModule,
        MatMenuModule,
        MatTooltipModule,
        MatChipsModule,
    TranslatePipe
  ],
    templateUrl: './notification-list.component.html',
    styleUrls: ['./notification-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationListComponent {
  private router = inject(Router);
  private readonly uiFeedback = inject(UIFeedbackService);
  private translate = inject(TranslateService);
  private readonly notificationsStore = inject(NOTIFICATIONS_STORE);

  readonly maxItems = input(10);
  readonly showGrouping = input(true);
  readonly showActions = input(true);
  readonly compact = input(false);

  readonly notificationClicked = output<Notification>();
  readonly actionClicked = output<Notification>();

  readonly notifications$: Observable<Notification[]> = toObservable(
    computed(() => [...this.notificationsStore.notifications()] as Notification[])
  );
  readonly groupedNotifications$: Observable<NotificationGrouping> = toObservable(
    computed(() => this.notificationsStore.grouped())
  );
  readonly summary$: Observable<NotificationSummary | null> = toObservable(this.notificationsStore.summary);

  readonly NotificationCategory = NotificationCategory;
  readonly NotificationPriority = NotificationPriority;

  /**
   * Handle notification click
   */
  onNotificationClick(notification: Notification): void {
    // Mark as read if not already read
    if (!notification.read) {
      this.notificationsStore.markAsRead(notification.notificationId)
        .then(() => console.log('Notification marked as read'))
        .catch(error => console.error('Error marking notification as read:', error));
    }

    // Navigate if action URL is provided
    if (notification.actionUrl) {
      void this.router.navigateByUrl(notification.actionUrl);
    }

    // Emit click event
    this.notificationClicked.emit(notification);
  }

  /**
   * Handle action button click
   */
  onActionClick(notification: Notification, event: Event): void {
    event.stopPropagation(); // Prevent notification click

    // Mark as read if not already read
    if (!notification.read) {
      this.notificationsStore.markAsRead(notification.notificationId).catch(() => { /* noop */ });
    }

    // Navigate if action URL is provided
    if (notification.actionUrl) {
      void this.router.navigateByUrl(notification.actionUrl);
    }

    // Emit action event
    this.actionClicked.emit(notification);
  }

  /**
   * Mark notification as read
   */
  markAsRead(notification: Notification, event: Event): void {
    event.stopPropagation();

    this.notificationsStore.markAsRead(notification.notificationId)
      .then(() => this.uiFeedback.toast.info(this.translate.instant('shared.notificationList.markedAsRead')))
      .catch(error => {
        console.error('Error marking notification as read:', error);
        this.uiFeedback.toast.error(this.translate.instant('shared.notificationList.errorMarkAsRead'));
      });
  }

  /**
   * Remove notification
   */
  removeNotification(notification: Notification, event: Event): void {
    event.stopPropagation();

    this.notificationsStore.remove(notification.notificationId)
      .then(() => this.uiFeedback.toast.success(this.translate.instant('shared.notificationList.removed')))
      .catch(error => {
        console.error('Error removing notification:', error);
        this.uiFeedback.toast.error(this.translate.instant('shared.notificationList.errorRemove'));
      });
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this.notificationsStore.markAllAsRead()
      .then(() => this.uiFeedback.toast.info(this.translate.instant('shared.notificationList.allMarkedAsRead')))
      .catch(error => {
        console.error('Error marking all notifications as read:', error);
        this.uiFeedback.toast.error(this.translate.instant('shared.notificationList.errorMarkAllAsRead'));
      });
  }

  /** Dev-only: create a few test notifications. Template keeps the button under a debug-only flag. */
  createTestNotifications(): void {
    this.notificationsStore.createTestNotifications(3)
      .then(response => this.uiFeedback.toast.success(response.message || this.translate.instant('shared.notificationList.testCreated')))
      .catch(error => {
        console.error('Error creating test notifications:', error);
        this.uiFeedback.toast.error(this.translate.instant('shared.notificationList.errorTestCreate'));
      });
  }

  /** Get icon for notification. Uses pure helper from the notifications utils. */
  getNotificationIcon(notification: Notification): string {
    return getNotificationIcon(notification);
  }

  /** Get color for notification. */
  getNotificationColor(notification: Notification): string {
    return getNotificationColor(notification);
  }

  /**
   * Get category label
   */
  getCategoryLabel(category: NotificationCategory): string {
    switch (category) {
      case NotificationCategory.FINANCIAL:
        return this.translate.instant('shared.notificationList.categoryFinancial');
      case NotificationCategory.CONTRACTS:
        return this.translate.instant('shared.notificationList.categoryContracts');
      case NotificationCategory.MAINTENANCE:
        return this.translate.instant('shared.notificationList.categoryMaintenance');
      case NotificationCategory.DOCUMENTS:
        return this.translate.instant('shared.notificationList.categoryDocuments');
      case NotificationCategory.SYSTEM:
        return this.translate.instant('shared.notificationList.categorySystem');
      case NotificationCategory.REMINDERS:
        return this.translate.instant('shared.notificationList.categoryReminders');
      default:
        return category;
    }
  }

  /**
   * Get priority label
   */
  getPriorityLabel(priority: NotificationPriority): string {
    switch (priority) {
      case NotificationPriority.URGENT:
        return this.translate.instant('shared.notificationList.priorityUrgent');
      case NotificationPriority.HIGH:
        return this.translate.instant('shared.notificationList.priorityHigh');
      case NotificationPriority.NORMAL:
        return this.translate.instant('shared.notificationList.priorityNormal');
      case NotificationPriority.LOW:
        return this.translate.instant('shared.notificationList.priorityLow');
      default:
        return priority;
    }
  }

  /**
   * Format relative time
   */
  getRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) {
      return this.translate.instant('shared.notificationList.timeJustNow');
    } else if (minutes < 60) {
      return this.translate.instant('shared.notificationList.timeMinutesAgo', { minutes });
    } else if (hours < 24) {
      return this.translate.instant('shared.notificationList.timeHoursAgo', { hours });
    } else if (days < 7) {
      return this.translate.instant('shared.notificationList.timeDaysAgo', { days });
    } else {
      return date.toLocaleDateString('fr-BE');
    }
  }

  /**
   * Track by function for performance
   */
  trackByNotificationId(index: number, notification: Notification): string {
    return notification.notificationId;
  }
} 